<template>
  <section class="patternmaker-sticker-tabs">
    <div
      v-if="enableSwiper"
      class="patternmaker-sticker-tabs-content"
    >
      <swiper
        :class="['patternmaker-sticker-tabs-content-swiper', {
          'patternmaker-sticker-tabs-content-swiper--no-loop': !tabsSwiper.loop,
        }]"
        :options="tabsSwiper"
        ref="tabsCarousel"
        @click="onTabClicked"
      >
        <swiper-slide
          v-for="(group, index) in stickerGroupsArray"
          :key="group.name"
          :class="['patternmaker-sticker-tabs-content-tab', `patternmaker-sticker-tabs-content-tab--${index}`, { active: group.name === currentStickerGroup }]"
          :data-group-name="group.name"
        >
          {{ group.display_name }}
          <span :class="['patternmaker-sticker-tabs-content-tab-active-marker', { 'patternmaker-sticker-tabs-content-tab-active-marker--active': group.name === currentStickerGroup }]">
            {{ group.display_name }}
          </span>
        </swiper-slide>
      </swiper>
      <div :class="['patternmaker-sticker-tabs-content-nav-wrapper', { 'hide': !enableCarouselNavigation }]">
        <button type="button" class="patternmaker-sticker-tabs-content-nav-button-previous"><span class="icon-arrow-left-indicator-open" slot="button-prev"></span></button>
        <button type="button" class="patternmaker-sticker-tabs-content-nav-button-next"><span class="icon-arrow-right-indicator-open" slot="button-next"></span></button>
      </div>
    </div>
  </section>
</template>

<script>
import { Swiper, SwiperSlide } from 'vue-awesome-swiper';

export default {
  name: "patternmakerStickerTabs",
  components: {
    Swiper,
    SwiperSlide,
  },
  props: {
    tabs: {
      default: () => [],
      type: Array
    }
  },
  data() {
    return {
      currentStickerGroup: "new",
      enableCarouselNavigation: true,
      enableSwiper: false,
      tabsSwiper: {
        slidesPerView: 3.25,
        slidesPerGroup: 1,
        loop: false,
        loopedSlides: 0,
        speed: 300,
        breakpoints: {
          768: {
            slidesPerView: 5,
          },
          1024: {
            slidesPerView: 7,
          },
        },
        shortSwipes: false,
        navigation: {
          prevEl: '.patternmaker-sticker-tabs-content-nav-button-previous',
          nextEl: '.patternmaker-sticker-tabs-content-nav-button-next',
        },
      },
      currentActiveIndex: -1,
    }
  },
  computed: {
    locale() {
      return this.$store.state.utils.configuration.locale;
    },
    stickerGroups() {
      return this.$store.state.utils.availableStickersByGroup;
    },
    currentAvailableStickersIds() {
      return this.currentAvailableStickers.map(sticker => sticker.id);
    },
    stickerGroupsArray() {
      return this.$props.tabs.map(tab => ({
        ...tab,
        display_name: tab.display_name.hasOwnProperty(this.locale) ? tab.display_name[this.locale] : tab.display_name.default
      }));
    }
  },
  watch: {
    stickerGroupsArray() {
      this.checkEnableCarouselNavigation();
    },
  },
  methods: {
    checkEnableCarouselNavigation() {
      let enableLoop;
      const viewportWidth = window.innerWidth;
      const breakpoints = this.tabsSwiper.breakpoints ? Object.keys(this.tabsSwiper.breakpoints).sort((a, b) => Number(a)-Number(b)) : [];
      if (!breakpoints.length || viewportWidth < Number(breakpoints[0])) {
        enableLoop = this.stickerGroupsArray.length > this.tabsSwiper.slidesPerView;
      }
      for (let i = 0; i < breakpoints.length - 1 && !enableLoop; i++) {
        if (viewportWidth >= Number(breakpoints[i]) && viewportWidth < Number(breakpoints[i + 1])) {
          enableLoop = this.stickerGroupsArray.length > this.tabsSwiper.breakpoints[breakpoints[i]].slidesPerView;
        }
      }
      if (!enableLoop && breakpoints.length && viewportWidth >= Number(breakpoints[breakpoints.length - 1])) {
        enableLoop = this.stickerGroupsArray.length > this.tabsSwiper.breakpoints[breakpoints[breakpoints.length - 1]].slidesPerView;
      }
      this.tabsSwiper.loop = enableLoop;
      if (enableLoop && this.stickerGroupsArray) this.tabsSwiper.loopedSlides = this.stickerGroupsArray.length;
      this.enableCarouselNavigation = enableLoop && window.innerWidth >= 768;
      this.enableSwiper = !!this.stickerGroupsArray.length;
    },
    onTabClicked(event) {
      const groupName = event.target.getAttribute('data-group-name');

      if (groupName) {
        this.updateSelectedTab(groupName);
        this.$eventBus.$emit('patternmaker-go-to-sticker', { groupName, moveToFirstStickerOfGroup: true });
        if (this.$store.getters['utils/useEcomDataLayerFormat']) {
          this.$patternMakerDatalayer.logAnalyticsEvent({
            action: 'Select Overlay Category',
            eventLabel: groupName,
            capitalized: true
          });
        }
      }
    },
    updateSelectedTab(groupName) {
      if (!groupName) return;
      const tab = this.stickerGroupsArray.find(group => group.name === groupName);
      const originalArrayIndex = this.stickerGroupsArray.indexOf(tab);

      const previousStickerGroup = this.currentStickerGroup;
      const previousActiveIndex = this.currentActiveIndex;
      const activeIndex = this.$refs.tabsCarousel.$swiper.activeIndex;
      this.currentStickerGroup = groupName;

      if (this.tabsSwiper.loop && (previousStickerGroup !== this.currentStickerGroup || previousActiveIndex !== activeIndex)) {
        const totalOriginalElements = this.stickerGroupsArray.length;
        const totalLoopedSlides = this.$refs.tabsCarousel.$swiper.loopedSlides || 0;
        let lowerPosition = originalArrayIndex + totalLoopedSlides - totalOriginalElements;
        let upperPosition = originalArrayIndex + totalLoopedSlides;
        if (lowerPosition < 0) { // advance to next 'window'
          lowerPosition += totalOriginalElements;
          upperPosition += totalOriginalElements;
        }
        const distanceToLowerPosition = Math.abs(activeIndex - lowerPosition);
        const distanceToUpperPosition = Math.abs(activeIndex - upperPosition);
        let position;
        // finds the closest position
        if (distanceToLowerPosition < distanceToUpperPosition && lowerPosition >= 0) {
          position = lowerPosition;
        } else {
          position = upperPosition;
        }
        // first try to use prev/next to avoid the jump caused by a sequence of calls to slideTo
        if (position === activeIndex - 1) {
          this.$refs.tabsCarousel.$swiper.slidePrev();
        } else if (position === activeIndex + 1) {
          this.$refs.tabsCarousel.$swiper.slideNext();
        } else {
          const slidedWithSuccess = this.$refs.tabsCarousel.$swiper.slideTo(position, this.tabsSwiper.speed, false);
          if (!slidedWithSuccess && position !== activeIndex) { // go back one 'window'
            position -= totalOriginalElements;
            this.$refs.tabsCarousel.$swiper.slideTo(position, this.tabsSwiper.speed, false);
          }
        }
        this.currentActiveIndex = position;
      }
    },
  },
  created() {
    this.$eventBus.$on('sticker-tab-selected', this.updateSelectedTab);
    window.addEventListener('resize', this.checkEnableCarouselNavigation);
  },
  mounted() {
    this.checkEnableCarouselNavigation();
  },
  beforeDestroy() {
    this.$eventBus.$off('sticker-tab-selected', this.updateSelectedTab);
    window.removeEventListener('resize', this.checkEnableCarouselNavigation);
  },
}
</script>
